import { StandardEvents } from "@shopify/events";
import { formatMoney } from "@theme/money-formatting";

/**
 * Keeps the bottom cart bar in step with the cart.
 *
 * The bar is printed by Liquid on first load, so this only has to handle changes
 * made without a page load: adding from a card, changing a quantity in the
 * drawer, removing a line.
 *
 * The figures come from /cart.js and are formatted with the theme's own money
 * formatter against the shop's money format, so the result matches what the
 * `money` filter would have produced on the server. That is a deliberate trade:
 * re-rendering a section would let the server do the formatting, but it costs a
 * request that returns markup we already have and would replace the element the
 * listeners are attached to.
 */
class RdpCartBar extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#onCartUpdate,
      {
        signal: this.#abortController.signal,
      },
    );
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    // The event fires before the request completes, so the cart is only settled
    // once its promise resolves. On failure the cart is refetched anyway rather
    // than left showing a total that was never charged.
    event.promise?.then(() => this.#refresh()).catch(() => this.#refresh()) ??
      this.#refresh();
  };

  async #refresh() {
    let cart;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return;
      cart = await response.json();
    } catch {
      return;
    }

    const count = Number(cart.item_count) || 0;

    if (count === 0) {
      this.setAttribute("hidden", "");
      return;
    }

    this.removeAttribute("hidden");

    const countEl = this.querySelector("[data-cart-bar-count]");
    const totalEl = this.querySelector("[data-cart-bar-total]");

    if (countEl) {
      // Both wordings come from the markup so the copy lives in one place. The
      // placeholder is %count% rather than a Liquid-looking one, so Liquid passes
      // it through untouched instead of evaluating it to an empty string.
      const template = this.dataset[count === 1 ? "oneItem" : "otherItems"];
      countEl.textContent = template
        ? template.replace("%count%", String(count))
        : String(count);
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(
        Number(cart.total_price) || 0,
        this.dataset.moneyFormat || "{{amount}}",
        this.dataset.currency || cart.currency,
      );
    }
  }
}

if (!customElements.get("rdp-cart-bar")) {
  customElements.define("rdp-cart-bar", RdpCartBar);
}
